import { useState } from 'react'
import { PublicLayout } from '../components/layouts/Layout'
import { Card, Button, Input, Textarea, Select } from '../components/common/UI'
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react'

export default function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', subject: '', message: '', inquiryType: '' })
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    await new Promise(resolve => setTimeout(resolve, 2000))
    setLoading(false)
    setSubmitted(true)
    setFormData({ name: '', email: '', company: '', subject: '', message: '', inquiryType: '' })
  }

  const inquiryTypes = [
    { value: '', label: 'Select inquiry type' },
    { value: 'general', label: 'General Information' },
    { value: 'sales', label: 'Sales Inquiry' },
    { value: 'support', label: 'Technical Support' }
  ]

  return (
    <PublicLayout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">Get in Touch</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Ready to start advertising on BlastTime? We'd love to hear from you.</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <Card className="p-8">
                <h2 className="text-2xl font-bold mb-6">Contact Information</h2>
                <div className="space-y-6">
                  {[
                    { icon: Mail, title: 'Email', content: ['hello@xupoli.com', 'support@xupoli.com'], color: 'bg-blue-500' },
                    { icon: Phone, title: 'Phone', content: ['+1 (732) 555-0123', 'Mon-Fri 9AM-6PM EST'], color: 'bg-green-500' },
                    { icon: MapPin, title: 'Office', content: ['28 Sheffield Court', 'North Brunswick, NJ 08902'], color: 'bg-purple-500' },
                    { icon: Clock, title: 'Business Hours', content: ['Monday - Friday: 9:00 AM - 6:00 PM EST', 'Saturday - Sunday: Closed'], color: 'bg-orange-500' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start space-x-4">
                      <div className={`w-12 h-12 ${item.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <item.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                        {item.content.map((line, j) => <p key={j} className="text-gray-600">{line}</p>)}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
            <Card className="p-8">
              <h2 className="text-2xl font-bold mb-6">Send us a Message</h2>
              {submitted ? (
                <div className="text-center py-12">
                  <Send className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Message Sent!</h3>
                  <p className="text-gray-600 mb-4">We'll get back to you within 24 hours.</p>
                  <Button variant="outline" onClick={() => setSubmitted(false)}>Send Another</Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Full Name *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                    <Input label="Email *" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Company" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
                    <Select label="Inquiry Type *" value={formData.inquiryType} onChange={(e) => setFormData({ ...formData, inquiryType: e.target.value })} options={inquiryTypes} required />
                  </div>
                  <Input label="Subject *" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} required />
                  <Textarea label="Message *" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} rows={6} required />
                  <Button type="submit" className="w-full py-3 text-lg font-semibold" loading={loading} icon={Send}>Send Message</Button>
                </form>
              )}
            </Card>
          </div>
        </div>
      </div>
    </PublicLayout>
  )
}